// ===================================================================
// PA Grants — Checklist Settings (Admin)
// Manage project checklist items stored in paChecklistTemplates table
// ===================================================================

import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Title, Text, Paper, Table, Button, Group, TextInput, Select, Textarea,
  Stack, ActionIcon, LoadingOverlay, Switch, Badge, Modal,
} from '../../components/gab/GabComponents';
import {
  IconPlus, IconTrash, IconDeviceFloppy, IconGripVertical,
  IconPencil, IconArrowUp, IconArrowDown, IconSettings,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useModulePermission } from '../../hooks/useModulePermission';
import { TABLES, FIELDS, fetchTable, createRecord, updateRecord, deleteRecord } from '../../lib/api-switch';

interface ChecklistTemplate {
  Rid: number;
  itemKey: string;
  label: string;
  category: string;
  description: string;
  required: string;
  sortOrder: string;
  isActive: string;
  programType: string;
}

const CATEGORY_OPTIONS = [
  { value: 'document', label: 'Document' },
  { value: 'form', label: 'Form' },
  { value: 'review', label: 'Review' },
  { value: 'milestone', label: 'Milestone' },
];

const CATEGORY_COLORS: Record<string, string> = {
  document: 'var(--color-primary-700)', form: 'var(--data-viz-650)', review: '#F86A0B', milestone: 'var(--color-success)',
};

const PROGRAM_OPTIONS = [
  { value: 'pa', label: 'PA (Public Assistance)' },
  { value: 'hmgp', label: 'HMGP (Hazard Mitigation)' },
  { value: 'both', label: 'Both Programs' },
];

const F = FIELDS.paChecklistTemplates;

function buildFields(item: Omit<ChecklistTemplate, 'Rid'>): Array<[string, string]> {
  return [
    [F.itemKey, item.itemKey],
    [F.label, item.label],
    [F.category, item.category],
    [F.description, item.description],
    [F.required, item.required],
    [F.sortOrder, item.sortOrder],
    [F.isActive, item.isActive],
    [F.programType, item.programType],
  ];
}

export default function ChecklistSettings() {
  const { canAdmin } = useModulePermission('pagrants');
  const { pathname } = useLocation();
  const isHMGP = pathname.includes('/hmgp/');
  const [items, setItems] = useState<ChecklistTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState<ChecklistTemplate | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editCategory, setEditCategory] = useState('document');
  const [editDescription, setEditDescription] = useState('');
  const [editRequired, setEditRequired] = useState(true);
  const [editProgram, setEditProgram] = useState('pa');

  // Add new
  const [addOpen, setAddOpen] = useState(false);
  const [addKey, setAddKey] = useState('');
  const [addLabel, setAddLabel] = useState('');
  const [addCategory, setAddCategory] = useState('document');
  const [addDescription, setAddDescription] = useState('');
  const [addRequired, setAddRequired] = useState(false);
  const [addProgram, setAddProgram] = useState('pa');

  const [filterProgram, setFilterProgram] = useState<string>(isHMGP ? 'hmgp' : 'pa');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const FC = FIELDS.paChecklistTemplates;
      const raw = await fetchTable<Record<string, unknown>>(TABLES.paChecklistTemplates);
      const rows: ChecklistTemplate[] = raw.map(r => ({
        Rid: Number(r.record_id) || 0,
        itemKey: String(r[FC.itemKey] ?? ''),
        label: String(r[FC.label] ?? ''),
        category: String(r[FC.category] ?? 'document'),
        description: String(r[FC.description] ?? ''),
        required: String(r[FC.required] ?? 'false'),
        sortOrder: String(r[FC.sortOrder] ?? '0'),
        isActive: String(r[FC.isActive] ?? 'true'),
        programType: String(r[FC.programType] ?? 'pa'),
      }));
      rows.sort((a, b) => (parseInt(a.sortOrder) || 0) - (parseInt(b.sortOrder) || 0));
      setItems(rows);
    } catch (_e) {
      notifications.show({ title: 'Error', message: 'Failed to load checklist templates', color: 'var(--color-error)' });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(i =>
    i.programType === filterProgram || i.programType === 'both'
  );

  // ── Move item up/down ─────────────────────────────────────────
  async function moveItem(rid: number, direction: 'up' | 'down') {
    const idx = filtered.findIndex(i => i.Rid === rid);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= filtered.length) return;

    setSaving(true);
    const a = filtered[idx];
    const b = filtered[swapIdx];
    const aOrder = a.sortOrder;
    const bOrder = b.sortOrder;

    try {
      await Promise.all([
        updateRecord(TABLES.paChecklistTemplates, a.Rid, [[F.sortOrder, bOrder]]),
        updateRecord(TABLES.paChecklistTemplates, b.Rid, [[F.sortOrder, aOrder]]),
      ]);
      await load();
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to reorder', color: 'var(--color-error)' });
    }
    setSaving(false);
  }

  // ── Toggle active ─────────────────────────────────────────────
  async function toggleActive(item: ChecklistTemplate) {
    setSaving(true);
    const newVal = item.isActive === 'true' ? 'false' : 'true';
    try {
      await updateRecord(TABLES.paChecklistTemplates, item.Rid, [[F.isActive, newVal]]);
      await load();
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to update', color: 'var(--color-error)' });
    }
    setSaving(false);
  }

  // ── Toggle required ───────────────────────────────────────────
  async function toggleRequired(item: ChecklistTemplate) {
    setSaving(true);
    const newVal = item.required === 'true' ? 'false' : 'true';
    try {
      await updateRecord(TABLES.paChecklistTemplates, item.Rid, [[F.required, newVal]]);
      await load();
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to update', color: 'var(--color-error)' });
    }
    setSaving(false);
  }

  // ── Edit ──────────────────────────────────────────────────────
  function openEdit(item: ChecklistTemplate) {
    setEditItem(item);
    setEditLabel(item.label);
    setEditCategory(item.category);
    setEditDescription(item.description);
    setEditRequired(item.required === 'true');
    setEditProgram(item.programType || 'pa');
    setEditOpen(true);
  }

  async function saveEdit() {
    if (!editItem) return;
    setSaving(true);
    try {
      await updateRecord(TABLES.paChecklistTemplates, editItem.Rid, [
        [F.label, editLabel],
        [F.category, editCategory],
        [F.description, editDescription],
        [F.required, String(editRequired)],
        [F.programType, editProgram],
      ]);
      setEditOpen(false);
      notifications.show({ title: 'Updated', message: `"${editLabel}" saved`, color: 'var(--color-success)' });
      await load();
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to save', color: 'var(--color-error)' });
    }
    setSaving(false);
  }

  // ── Add new ───────────────────────────────────────────────────
  function openAdd() {
    setAddKey('');
    setAddLabel('');
    setAddCategory('document');
    setAddDescription('');
    setAddRequired(false);
    setAddProgram(filterProgram);
    setAddOpen(true);
  }

  async function saveAdd() {
    if (!addKey.trim() || !addLabel.trim()) {
      notifications.show({ title: 'Missing fields', message: 'Key and label are required', color: '#F86A0B' });
      return;
    }
    // Check for duplicate key
    if (items.some(i => i.itemKey === addKey.trim())) {
      notifications.show({ title: 'Duplicate key', message: `"${addKey}" already exists`, color: '#F86A0B' });
      return;
    }
    setSaving(true);
    const nextSort = String((filtered.length + 1) * 10);
    try {
      await createRecord(TABLES.paChecklistTemplates, buildFields({
        itemKey: addKey.trim(),
        label: addLabel.trim(),
        category: addCategory,
        description: addDescription.trim(),
        required: String(addRequired),
        sortOrder: nextSort,
        isActive: 'true',
        programType: addProgram,
      }));
      setAddOpen(false);
      notifications.show({ title: 'Added', message: `"${addLabel}" created`, color: 'var(--color-success)' });
      await load();
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to create', color: 'var(--color-error)' });
    }
    setSaving(false);
  }

  // ── Delete ────────────────────────────────────────────────────
  async function handleDelete(item: ChecklistTemplate) {
    if (!confirm(`Delete "${item.label}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await deleteRecord(TABLES.paChecklistTemplates, item.Rid);
      notifications.show({ title: 'Deleted', message: `"${item.label}" removed`, color: '#939598' });
      await load();
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to delete', color: 'var(--color-error)' });
    }
    setSaving(false);
  }

  if (!canAdmin) {
    return (
      <Stack align="center" mt="xl">
        <IconSettings size={48} color="#939598" />
        <Text c="dimmed">Admin access required to manage checklist settings.</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="md" pos="relative">
      <LoadingOverlay visible={loading || saving} />

      <Group justify="space-between">
        <div>
          <Title order={3}>Checklist Settings</Title>
          <Text size="sm" c="dimmed">Manage project checklist items. Changes apply to all new and existing projects.</Text>
        </div>
        <Group gap="sm">
          <Select
            size="xs"
            value={filterProgram}
            onChange={v => setFilterProgram(v || 'pa')}
            data={[{ value: 'pa', label: 'PA Items' }, { value: 'hmgp', label: 'HMGP Items' }]}
            style={{ width: 140 }}
          />
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openAdd}>
            Add Item
          </Button>
        </Group>
      </Group>

      <Paper p="md" withBorder style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
        <Table striped highlightOnHover>
          <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
            <Table.Tr>
              <Table.Th style={{ width: 60 }}>Order</Table.Th>
              <Table.Th>Label</Table.Th>
              <Table.Th style={{ width: 90 }}>Category</Table.Th>
              <Table.Th style={{ width: 80 }}>Required</Table.Th>
              <Table.Th style={{ width: 70 }}>Active</Table.Th>
              <Table.Th style={{ width: 80 }}>Program</Table.Th>
              <Table.Th style={{ width: 120 }}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text size="sm" c="dimmed" ta="center" py="lg">
                    No checklist items for {filterProgram.toUpperCase()}. Click "Add Item" to create one.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {filtered.map((item, idx) => (
              <Table.Tr key={item.Rid} style={{ opacity: item.isActive === 'true' ? 1 : 0.5 }}>
                <Table.Td>
                  <Group gap={2}>
                    <IconGripVertical size={14} color="#939598" />
                    <ActionIcon size="xs" variant="subtle" color="#939598"
                      disabled={idx === 0}
                      onClick={() => moveItem(item.Rid, 'up')}>
                      <IconArrowUp size={12} />
                    </ActionIcon>
                    <ActionIcon size="xs" variant="subtle" color="#939598"
                      disabled={idx === filtered.length - 1}
                      onClick={() => moveItem(item.Rid, 'down')}>
                      <IconArrowDown size={12} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={500}>{item.label}</Text>
                  {item.description && (
                    <Text size="xs" c="dimmed" lineClamp={1}>{item.description}</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" color={CATEGORY_COLORS[item.category] || '#939598'} variant="light">
                    {item.category}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Switch
                    size="xs"
                    checked={item.required === 'true'}
                    onChange={() => toggleRequired(item)}
                    color="var(--color-error)"
                  />
                </Table.Td>
                <Table.Td>
                  <Switch
                    size="xs"
                    checked={item.isActive === 'true'}
                    onChange={() => toggleActive(item)}
                  />
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" variant="outline" color="#939598">{item.programType}</Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <ActionIcon size="sm" variant="subtle" color="var(--color-primary-700)" onClick={() => openEdit(item)}>
                      <IconPencil size={14} />
                    </ActionIcon>
                    <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => handleDelete(item)}>
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        </div>
      </Paper>

      <Text size="xs" c="dimmed">
        {items.length} total items ({items.filter(i => i.isActive === 'true').length} active).
        Items marked inactive will not appear on project checklists.
      </Text>

      {/* ── Edit Modal ─────────────────────────────────────────── */}
      <Modal opened={editOpen} onClose={() => setEditOpen(false)} title="Edit Checklist Item" size="md">
        <Stack gap="sm">
          <TextInput label="Key" value={editItem?.itemKey || ''} disabled description="Unique identifier (cannot be changed)" />
          <TextInput label="Label" value={editLabel} onChange={e => setEditLabel(e.target.value)} required />
          <Select label="Category" value={editCategory} onChange={v => setEditCategory(v || 'document')} data={CATEGORY_OPTIONS} />
          <Textarea label="Description" value={editDescription} onChange={e => setEditDescription(e.target.value)} autosize minRows={2} />
          <Select label="Program" value={editProgram} onChange={v => setEditProgram(v || 'pa')} data={PROGRAM_OPTIONS} />
          <Switch label="Required" checked={editRequired} onChange={e => setEditRequired(e.currentTarget.checked)} color="var(--color-error)" />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button leftSection={<IconDeviceFloppy size={14} />} onClick={saveEdit} loading={saving}>Save</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Add Modal ──────────────────────────────────────────── */}
      <Modal opened={addOpen} onClose={() => setAddOpen(false)} title="Add Checklist Item" size="md">
        <Stack gap="sm">
          <TextInput
            label="Key"
            value={addKey}
            onChange={e => setAddKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
            required
            description="Unique snake_case identifier (e.g. site_inspection)"
          />
          <TextInput label="Label" value={addLabel} onChange={e => setAddLabel(e.target.value)} required />
          <Select label="Category" value={addCategory} onChange={v => setAddCategory(v || 'document')} data={CATEGORY_OPTIONS} />
          <Textarea label="Description" value={addDescription} onChange={e => setAddDescription(e.target.value)} autosize minRows={2} />
          <Select label="Program" value={addProgram} onChange={v => setAddProgram(v || 'pa')} data={PROGRAM_OPTIONS} />
          <Switch label="Required" checked={addRequired} onChange={e => setAddRequired(e.currentTarget.checked)} color="var(--color-error)" />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button leftSection={<IconPlus size={14} />} onClick={saveAdd} loading={saving}>Add</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
